/**
 * Daily Reveal prop market registry — DraftKings Sportsbook Partners mappings.
 *
 * Prefer stable `market.marketType.id` values verified via live capture
 * (docs/daily-reveal-prop-market-mapping.md). Regex aliases are fallback only.
 */

import type { DailyRevealPropOperator } from './daily-reveal';
import type { SportCode } from './leagues';

export type PropMarketImplementationStatus =
  | 'verified_settlement_ready'
  | 'verified_settlement_missing'
  | 'inferred'
  | 'needs_live_response'
  | 'unsupported';

export type PropMarketOutcomeType =
  | 'over_under'
  | 'yes_no'
  | 'milestone'
  | 'one_sided'
  | 'scorer_list';

export type PropMarketSelectionMode =
  | 'over'
  | 'milestone_n_plus'
  | 'yes'
  | 'scorer_list'
  | 'x_or_fewer';

export type PropMarketRole = 'batter' | 'pitcher' | 'offense' | 'defense' | 'scorer' | 'any';

export type PropMarketDefinition = {
  sport: SportCode | 'SOCCER';
  /**
   * Whose stat the market is about. Absent means `player`, which every definition here was
   * until team markets were added — so existing entries keep their meaning untouched.
   *
   * This is a SCOPE on the definition rather than a loosening of the guards below, and it has
   * to be: those guards are what keep team totals out of Daily Reveal's player-prop flow
   * (`TEAM_OR_GAME_EXCLUSIONS`, `requirePlayerPropsTag`). Relaxing them globally to let a team
   * market through would pull "Team Total Runs" into every player definition that shares a
   * name fragment with it.
   */
  scope?: 'player' | 'team';
  key: string;
  label: string;
  /** Verified Partners `market.marketType.id` values. */
  providerMarketTypeIds: string[];
  /** Fallback display-name fragments when type id is absent. */
  providerAliases: string[];
  /** Exact marketType.name values (case-insensitive match). */
  providerMarketTypeNames: string[];
  contextGuards: {
    requirePlayerPropsTag?: boolean;
    /** DK tags a team market `TeamProps`, exactly as it tags a player market `PlayerProps`. */
    requireTeamPropsTag?: boolean;
    excludeNamePatterns?: RegExp[];
    role?: PropMarketRole;
    selectionModes: PropMarketSelectionMode[];
  };
  outcomeType: PropMarketOutcomeType;
  lineRequired: boolean;
  defaultOperator: DailyRevealPropOperator;
  settlementStatTypes: string[];
  /**
   * How the market settles ACROSS the codes above, when one code is not enough.
   *
   * Absent — the default — means any single code settles it on its own, which is true of every
   * market that lists one code and of the several that list spelling variants of one stat.
   *
   *  - `sum`: the codes are components to add up. All-purpose yards is rushing PLUS receiving.
   *  - `any`: the codes are alternatives, and the market settles if ANY of them is hit. Anytime
   *    touchdown is rushing OR receiving OR a return; the feed publishes no total that counts
   *    only the touchdowns a player SCORED, so the codes have to be read individually.
   *
   * The distinction is invisible in the array itself and decides what a consumer may do with
   * it. A builder that puts ONE stat code on a tile can express neither: `sum` needs the
   * components added, `any` needs all of them checked. `sum` additionally must not be used to
   * PRICE a tile — see the pricing map in BingoMarketMatchLogic — whereas any single `any` code
   * prices its market perfectly well, because they all describe the same event.
   */
  settlementCombines?: 'sum' | 'any';
  /**
   * Entries in `settlementStatTypes` that are an AGGREGATE of the others rather than a component
   * of the event — `total_touchdowns` beside `rush_touchdowns` and `receive_touchdowns`.
   *
   * They are kept in `settlementStatTypes` because they still match a market, but they must
   * never reach a consumer that ADDS the list up: summing an aggregate alongside its own
   * components double-counts every score. The feed DOES publish `total_touchdowns` (it is a
   * rollup the feed service computes, not a Sportradar field), so this carve-out is load-bearing
   * rather than theoretical.
   */
  settlementAggregateStatTypes?: string[];
  /**
   * Entries in `settlementStatTypes` that SETTLE the market but must never PRICE or BUILD a tile.
   *
   * A kick return touchdown settles Anytime Touchdown — the sportsbook pays it — but "kick return
   * touchdowns over 0.5" is a far rarer event than "any touchdown", so letting that code reach the
   * pricing map would hang an anytime-TD price on a return-TD tile and drop it in the wrong
   * difficulty band. Settlement must be generous and pricing must be exact, and this is the only
   * place the two lists differ.
   */
  settlementOnlyStatTypes?: string[];
  implementationStatus: PropMarketImplementationStatus;
};

/** A definition's scope, defaulting to the player markets this registry started as. */
export const definitionScope = (def: PropMarketDefinition): 'player' | 'team' =>
  def.scope ?? 'player';

const TEAM_OR_GAME_EXCLUSIONS = [
  /\bteam total\b/i,
  /\btotal hits\b/i,
  /\btotal runs\b/i,
  /\bgame total\b/i,
  /\bboth teams\b/i,
  /\brace to\b/i,
];

/*
 * Near kickoff DK ships 1st Half / 1st Quarter variants of every NFL player prop
 * ("Receiving Yards O/U - 1st Half" 15522, "- 1st Quarter" 15516, …), all tagged
 * PlayerProps. They price part of a game against our full-game settlement stats, so no
 * full-game definition may ever claim one. The id-over-alias rule blocks them whenever
 * the payload declares a type id; these patterns cover the alias fallback when it doesn't.
 */
const PERIOD_LIMITED_EXCLUSIONS = [
  /\b(?:1st|2nd|first|second)\s+half\b/i,
  /\b(?:1st|2nd|3rd|4th|first|second|third|fourth)\s+quarter\b/i,
];

/*
 * Basketball combines almost every counting stat into its own priced market ("Points +
 * Rebounds", "Points + Rebounds + Assists", "Rebounds + Assists"). Each combo is a DISTINCT
 * market with its own settlement, so a single-stat definition must never claim one: without
 * this, `points` matches "Points + Rebounds O/U" on the word "Points" and freezes a two-stat
 * line to be graded against points alone.
 */
const BASKETBALL_COMBINED_EXCLUSIONS = [/\+/];

/*
 * Basketball markets that name a stat but are not that player's total for it — head-to-head
 * comparisons between two players, race-to-N, first-scorer and wire-to-wire. They are priced
 * on a different question entirely and several are not even player props.
 */
const BASKETBALL_NON_TOTAL_EXCLUSIONS = [
  /\bh2h\b/i,
  /\bmost\s+(?:points|rebounds|assists)\b/i,
  /\brace to\b/i,
  /\b(?:1st|first)\s+points\s+scorer\b/i,
  /\bwire to wire\b/i,
  /\blargest lead\b/i,
];

export const PROP_MARKET_REGISTRY: PropMarketDefinition[] = [
  // ── MLB settlement-ready ─────────────────────────────────────────────
  {
    sport: 'MLB',
    key: 'hitting_h',
    label: 'Hits',
    providerMarketTypeIds: ['14035', '13227'],
    providerMarketTypeNames: ['Hits O/U', 'Hits Milestones'],
    providerAliases: ['hits o/u', ' hits'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'batter',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        /\bhits allowed\b/i,
        /\bextra base hits\b/i,
        /\bhits \+ runs\b/i,
        /\bhits \+ stolen\b/i,
        /\bhits \+ walks\b/i,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['hitting_h'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'MLB',
    key: 'hitting_runs',
    label: 'Runs',
    providerMarketTypeIds: ['14027', '14463'],
    providerMarketTypeNames: ['Runs O/U', 'Runs (Batter) Milestones'],
    providerAliases: ['runs o/u', 'runs (batter)'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'batter',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        /\bruns \+ rbis?\b/i,
        /\bhits \+ runs\b/i,
        /\bearned runs\b/i,
        /\bruns allowed\b/i,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['hitting_runs'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'MLB',
    key: 'hitting_rbi',
    label: 'RBIs',
    providerMarketTypeIds: ['14023', '13230'],
    providerMarketTypeNames: ['RBIs O/U', 'RBIs Milestones'],
    providerAliases: ['rbis o/u', 'rbis'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'batter',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [/\bhits \+ runs \+ rbis?\b/i, /\bruns \+ rbis?\b/i],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['hitting_rbi'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'MLB',
    key: 'hitting_hr',
    label: 'Home Runs',
    providerMarketTypeIds: ['13233'],
    providerMarketTypeNames: ['Home Runs Milestones'],
    providerAliases: ['home runs'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'batter',
      selectionModes: ['milestone_n_plus'],
      excludeNamePatterns: [/1st 5 inning/i, /\brace to\b/i],
    },
    outcomeType: 'milestone',
    lineRequired: false,
    defaultOperator: 'over_or_true',
    settlementStatTypes: ['hitting_hr'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'MLB',
    key: 'hitting_bb',
    label: 'Walks',
    providerMarketTypeIds: ['14039', '14469'],
    providerMarketTypeNames: ['Walks (Batter) O/U', 'Walks (Batter) Milestones'],
    providerAliases: ['walks (batter)'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'batter',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [/\bwalks allowed\b/i, /\bhits \+ walks\b/i],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['hitting_bb'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'MLB',
    key: 'hitting_sb',
    label: 'Stolen Bases',
    providerMarketTypeIds: ['14024', '14461'],
    providerMarketTypeNames: ['Stolen Bases O/U', 'Stolen Bases Milestones'],
    providerAliases: ['stolen bases'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'batter',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [/\bhits \+.*stolen\b/i, /\bruns \+.*stolen\b/i],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_true',
    settlementStatTypes: ['hitting_sb'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'MLB',
    key: 'pitching_k',
    label: 'Pitcher Strikeouts',
    providerMarketTypeIds: ['14018', '13228'],
    providerMarketTypeNames: ['Strikeouts Thrown O/U', 'Strikeouts Thrown Milestones'],
    providerAliases: ['strikeouts thrown'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'pitcher',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [/\bstrikeouts \(batter\)\b/i],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['pitching_k'],
    implementationStatus: 'verified_settlement_ready',
  },

  // ── MLB team markets (Bingo) ──────────────────────────────────────────
  /*
   * A Bingo tile can play a TEAM's stat, and these are the markets that price one. They are
   * exactly what every player definition above EXCLUDES by name (`TEAM_OR_GAME_EXCLUSIONS`), so
   * they carry `scope: 'team'` and none of the player guards — for these the team total is the
   * thing being matched rather than the thing being avoided.
   *
   * IDS AND NAMES ARE FROM A LIVE CAPTURE (2026-08-26, MLB league 84240), not inferred. That
   * mattered more than usual, because two plausible guesses were both wrong:
   *
   *   - An alias of `team total runs` ALSO matches "Team Total Runs - 1st 5 Innings" (3_1906),
   *     a different market with a different line — 2.5 where the full game is 3.5. It would
   *     have priced tiles from the wrong market and banded them as the wrong difficulty, which
   *     is the exact failure the id-over-alias rule at the top of this file exists to prevent.
   *     Hence the explicit inning exclusions.
   *   - The team is NOT on `participantId`: `hasParticipantOnMarket` is false and the selections
   *     are a bare "Over"/"Under". The team is the market NAME's prefix — "ARI Diamondbacks:
   *     Team Total Runs" — so whatever consumes these must read the subject from the name.
   *
   * `TeamProps` is a real DK tag on both, and is the clean discriminator.
   */
  {
    sport: 'MLB',
    scope: 'team',
    key: 'team_total_runs',
    label: 'Team Total Runs',
    // Verified live: "ARI Diamondbacks: Team Total Runs", Over 3.5 at -115.
    providerMarketTypeIds: ['3_2066'],
    providerMarketTypeNames: ['Team Total Runs'],
    // No alias: every plausible one also matches the inning variants.
    providerAliases: [],
    contextGuards: {
      requireTeamPropsTag: true,
      selectionModes: ['over'],
      excludeNamePatterns: [
        /\b1st \d+ innings?\b/i,
        /\bgame total\b/i,
        /\bboth teams\b/i,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over_or_equal',
    // The FEED's team stat code — `runs`, not the batter's `hitting_runs`.
    settlementStatTypes: ['runs'],
    implementationStatus: 'inferred',
  },
  {
    sport: 'MLB',
    scope: 'team',
    key: 'team_total_hits',
    label: 'Team Total Hits',
    // Verified live: "LA Angels: Team Total Hits", Over 7.5 at +105.
    providerMarketTypeIds: ['3_1788'],
    providerMarketTypeNames: ['Team Total Hits'],
    providerAliases: [],
    contextGuards: {
      requireTeamPropsTag: true,
      selectionModes: ['over'],
      excludeNamePatterns: [
        /\b1st \d+ innings?\b/i,
        /\bgame total\b/i,
        /\bboth teams\b/i,
        // "Total Hits" (3_1874) is the GAME's hits, both teams combined.
        /^total hits\b/i,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['hits'],
    implementationStatus: 'inferred',
  },

  // ── NFL settlement-ready ─────────────────────────────────────────────
  {
    sport: 'NFL',
    key: 'anytime_touchdown',
    label: 'Anytime Touchdown',
    providerMarketTypeIds: ['11019'],
    providerMarketTypeNames: ['Anytime Touchdown Scorer'],
    providerAliases: ['anytime td', 'anytime touchdown', 'to score a touchdown'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'scorer',
      selectionModes: ['scorer_list'],
      // The half/quarter variants (11024 "Anytime Touchdown Scorer - 1st Half", 11025
      // "- 2nd Half", 11026 "- 1st Quarter") alias-match "anytime td" but price a
      // different bet, and their selections never pass the scorer eligibility check.
      // Left unexcluded they "cover" this key in the bulk slice, which skipped detail
      // hydration and froze WNF days with zero anytime TD rows.
      excludeNamePatterns: [
        /passing td/i,
        /pass td/i,
        /rushing td/i,
        /receiving td/i,
        ...PERIOD_LIMITED_EXCLUSIONS,
      ],
    },
    outcomeType: 'scorer_list',
    lineRequired: false,
    defaultOperator: 'over_or_true',
    /*
     * EVERY WAY A PLAYER CAN SCORE ONE HIMSELF — rushing, receiving, or carrying a return into
     * the end zone. The sportsbook pays a kick or punt return touchdown on this market, so
     * settling on rushing and receiving alone graded a returner's tile a loss on a touchdown he
     * plainly scored. The return codes are listed in `settlementOnlyStatTypes` below: they
     * settle the market but must not price a tile.
     *
     * PASSING TOUCHDOWNS ARE DELIBERATELY ABSENT. This market is "Anytime Touchdown SCORER":
     * the player has to reach the end zone himself. A quarterback who throws three of them has
     * scored none, and QBs are in the pool for this market (a sneak is a real touchdown — see
     * DEFAULT_POSITIONS_BY_MARKET_KEY), so listing `pass_touchdowns` would mark nearly every
     * starting quarterback's tile in nearly every game and settle nothing like the price the
     * book quoted. Passing touchdowns settle `passing_touchdowns`, which is its own market.
     *
     * `pass_int_touchdowns` — a pick-six THROWN — is absent for the mirror-image reason: that
     * touchdown belongs to the defender who ran it back, and he is credited
     * `int_return_touchdowns`.
     *
     * `total_touchdowns` stays first for compatibility (removing a candidate could un-match
     * something relying on it) but settles NOTHING: the feed service's rollup adds
     * `pass_touchdowns`, so reading it is exactly the passing-touchdown bug above. It is named
     * in `settlementAggregateStatTypes`, which drops it from `anySettlementFeedCodesFor`, and
     * omitted from Bingo's KNOWN_FEED_CODES, which drops it from pricing.
     */
    settlementStatTypes: [
      'total_touchdowns',
      'rush_touchdowns',
      'receive_touchdowns',
      'kick_return_touchdowns',
      'punt_return_touchdowns',
      'int_return_touchdowns',
      'misc_return_touchdowns',
    ],
    // The total OF the codes that follow it — and, because the feed's rollup also folds in
    // passing, not even a faithful one. Named so `anySettlementFeedCodesFor` can keep its
    // promise that the list it returns may be summed.
    settlementAggregateStatTypes: ['total_touchdowns'],
    /*
     * Returns settle this market but must never price it. An anytime-TD price on a
     * "kick return touchdown over 0.5" tile is off by an order of magnitude, and Bingo's bands
     * are drawn from the price.
     */
    settlementOnlyStatTypes: [
      'kick_return_touchdowns',
      'punt_return_touchdowns',
      'int_return_touchdowns',
      'misc_return_touchdowns',
    ],
    /*
     * The codes are disjoint — Sportradar records a touchdown in exactly one category — so a
     * grader ADDS them and gets the player's scored-touchdown count. A single-code tile cannot:
     * built on `rush_touchdowns`, it grades a receiver's touchdown as a miss. Bingo settles it
     * across all of them — see `anySettlementFeedCodesFor` and `BingoPlayerOverUnderGrader`.
     */
    settlementCombines: 'any',
    implementationStatus: 'verified_settlement_ready',
  },

  // ── NFL verified (live capture 2026-09-04, league 88808) ─────────────
  /*
   * IDS AND NAMES ARE FROM A LIVE CAPTURE (2026-09-04, NFL league 88808, Week 1 slate with
   * full player props posted) — docs/daily-reveal-prop-market-samples/nfl.capture.json.
   *
   * Each yardage market exists in two shapes on the Partners API: an O/U
   * (`OverUnderSingleLine`, Over/Under selections carrying the line) and a "Milestones"
   * ladder (`AlphanumericList`, selections named "10+", "15+", …). Only the O/U ids are
   * registered for the line-required keys below — their `selectionModes: ['over']` would
   * silently drop every milestone selection anyway, so registering the milestone id would
   * match a market and then freeze nothing from it. Milestone ids, for when a milestone
   * mode is deliberately added: rushing 12332, passing 12330, receiving 12331,
   * rush+rec 12333, receptions 14382.
   */
  {
    sport: 'NFL',
    key: 'rushing_yards',
    label: 'Rushing Yards',
    // Verified live: "Sam Darnold Rushing Yards O/U", Over 60.5 at -112.
    providerMarketTypeIds: ['13675'],
    providerMarketTypeNames: ['Rushing Yards O/U'],
    providerAliases: ['rushing yards', 'rush yards', 'rush yds'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'offense',
      selectionModes: ['over'],
      // DK's combined markets spell it out — "Rushing + Receiving Yards O/U" (13680),
      // "Passing + Rushing Yards O/U" (13690) — and both alias-match "rushing yards",
      // so both long forms must be excluded, not just the abbreviated "rush + rec".
      excludeNamePatterns: [
        /all.?purpose/i,
        /rush(?:ing)?\s*\+\s*rec/i,
        /pass(?:ing)?\s*\+\s*rush/i,
        ...PERIOD_LIMITED_EXCLUSIONS,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['rushing_yards', 'rush_yards'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'NFL',
    key: 'passing_yards',
    label: 'Passing Yards',
    // Verified live: "Sam Darnold Passing Yards O/U", Over 229.5 at -113.
    providerMarketTypeIds: ['13552'],
    providerMarketTypeNames: ['Passing Yards O/U'],
    providerAliases: ['passing yards', 'pass yards', 'pass yds'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'offense',
      selectionModes: ['over'],
      excludeNamePatterns: [/pass(?:ing)?\s*\+\s*rush/i, ...PERIOD_LIMITED_EXCLUSIONS],
    },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['passing_yards', 'pass_yards'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'NFL',
    key: 'receiving_yards',
    label: 'Receiving Yards',
    // Verified live: "Rashid Shaheed Receiving Yards O/U", Over 31.5 at -110.
    providerMarketTypeIds: ['13674'],
    providerMarketTypeNames: ['Receiving Yards O/U'],
    providerAliases: ['receiving yards', 'rec yards', 'rec yds', 'reception yards', 'recv yards'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'offense',
      selectionModes: ['over'],
      // "Rushing + Receiving Yards O/U" (13680) alias-matches "receiving yards"; the long
      // form must be excluded — /rush \+ rec/ alone missed "Rushing + Receiving".
      excludeNamePatterns: [/all.?purpose/i, /rush(?:ing)?\s*\+\s*rec/i, ...PERIOD_LIMITED_EXCLUSIONS],
    },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over_or_equal',
    // Neither `receiving_yards` nor `rec_yards` is a code the feed publishes; it calls this
    // `receive_yards`. Added rather than swapped, so nothing that matches today stops matching.
    settlementStatTypes: ['receiving_yards', 'rec_yards', 'receive_yards'],
    implementationStatus: 'verified_settlement_ready',
  },
  /*
   * RECEPTIONS. Added because the registry did not carry it and Bingo did — deriving Bingo's
   * pricing map from this list would otherwise have made NFL receptions unpriceable, which an
   * equivalence check caught before the switch.
   *
   * `receive_receptions` is verified against /stat-types/NFL. The PROVIDER mapping is now
   * verified too: the 2026-09-04 live capture carries "Receptions O/U" as marketType.id
   * `13676` ("Rashid Shaheed Receptions O/U", Over 7.5 at -122), tagged PlayerProps.
   */
  {
    sport: 'NFL',
    key: 'receptions',
    label: 'Receptions',
    providerMarketTypeIds: ['13676'],
    providerMarketTypeNames: ['Receptions O/U'],
    providerAliases: ['receptions', 'total receptions', 'receiving receptions'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'offense',
      selectionModes: ['over'],
      // "Receiving yards" shares the word; the yardage markets are a different thing entirely.
      excludeNamePatterns: [/yards/i, /yds/i, ...PERIOD_LIMITED_EXCLUSIONS],
    },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['receive_receptions'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'NFL',
    key: 'passing_touchdowns',
    label: 'Passing Touchdowns',
    // Verified live: "Drake Maye Passing Touchdowns O/U", Over 1.5 at +144; milestones
    // ladder ("1+" -389, "2+" +143) included because this key already freezes
    // milestone_n_plus selections.
    providerMarketTypeIds: ['12809', '13944'],
    providerMarketTypeNames: ['Passing Touchdowns O/U', 'Passing Touchdowns Milestones'],
    providerAliases: ['passing td', 'pass td', 'passing touchdowns'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'offense',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [/anytime/i, /to score/i, /scorer/i, ...PERIOD_LIMITED_EXCLUSIONS],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['passing_touchdowns', 'pass_touchdowns'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'NFL',
    key: 'all_purpose_yards',
    label: 'All-Purpose Yards',
    // Verified live: "TreVeyon Henderson Rushing + Receiving Yards O/U", Over 40.5 at -114.
    // DK's name for this stat is "Rushing + Receiving Yards"; there is no separate
    // kick/punt-return-inclusive all-purpose market on the Partners slate.
    providerMarketTypeIds: ['13680'],
    providerMarketTypeNames: ['Rushing + Receiving Yards O/U'],
    providerAliases: ['all-purpose yards', 'all purpose yards', 'rush + rec yards', 'rushing + receiving'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'offense',
      selectionModes: ['over'],
      excludeNamePatterns: [...PERIOD_LIMITED_EXCLUSIONS],
    },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over_or_equal',
    // `rush_yards` and `receive_yards` are the codes the feed actually publishes; the unprefixed
    // spellings are kept alongside rather than removed.
    settlementStatTypes: ['rushing_yards', 'rush_yards', 'receiving_yards', 'rec_yards', 'receive_yards'],
    settlementCombines: 'sum',
    implementationStatus: 'verified_settlement_ready',
  },

  {
    sport: 'NFL',
    key: 'sacks',
    label: 'Sacks',
    // Absent from the 2026-09-04 capture; POSTED by 2026-09-08 on the same WNF event —
    // verified live: "Uchenna Nwosu Total Sacks O/U" (13684) and "Christian Barmore
    // Total Sacks" milestones (14394), both tagged PlayerProps. DK adds these late in
    // the week, so a day synced early may legitimately have none.
    providerMarketTypeIds: ['13684', '14394'],
    providerMarketTypeNames: ['Sacks O/U', 'Sacks Milestones'],
    providerAliases: ['sacks', 'sack'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'defense',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [/team total/i, /1st drive/i, /1st sack/i, ...PERIOD_LIMITED_EXCLUSIONS],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['defense_sacks', 'sacks'],
    implementationStatus: 'verified_settlement_ready',
  },

  /*
   * ── NBA / NCAAMB ────────────────────────────────────────────────────
   *
   * `providerMarketTypeIds` captured live from the DK Sportsbook Partners API on 2026-09-16.
   *
   * WHY THE IDS CAME FROM A WNBA SLATE. DK prices no NBA player props until close to the
   * season opener (the live NBA league 42648 carried 42 fixtures that day and NOT ONE player
   * prop — only Moneyline/Spread/Total), so there was no NBA slate to read them off. The ids
   * are nonetheless the right ones: `marketType.id` is a GLOBAL per-template identifier, not a
   * per-league one — NFL "Passing Yards Milestones" is 12330 and basketball "Points Milestones"
   * is 12189 in the same numeric namespace — and WNBA (league 94682) is the same basketball
   * sport (`sportId = 2`) rendering the same market templates.
   *
   * VERIFY THESE ON THE FIRST NBA SLATE (season opens 2026-10-20). Re-run
   * `scripts/daily-reveal-prop-market-capture` against NBA and confirm the ids below appear.
   * If DK ever did number a template differently per league, the failure is SILENT AND TOTAL
   * rather than partial: `marketMatchesDefinition` refuses to fall back to alias matching once
   * a definition carries ids and the payload declares a different one, so every pool would come
   * back empty rather than mismatched. That is the safe direction to fail, but it needs
   * catching before a contest goes live.
   */
  {
    sport: 'NBA',
    key: 'points',
    label: 'Points',
    providerMarketTypeIds: ['12660', '12189'],
    providerMarketTypeNames: ['Points O/U', 'Points Milestones'],
    providerAliases: ['points o/u', 'points milestones'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'any',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        ...PERIOD_LIMITED_EXCLUSIONS,
        ...BASKETBALL_COMBINED_EXCLUSIONS,
        ...BASKETBALL_NON_TOTAL_EXCLUSIONS,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['points'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'NBA',
    key: 'rebounds',
    label: 'Rebounds',
    providerMarketTypeIds: ['12662', '12191'],
    providerMarketTypeNames: ['Rebounds O/U', 'Rebounds Milestones'],
    providerAliases: ['rebounds o/u', 'rebounds milestones'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'any',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        ...PERIOD_LIMITED_EXCLUSIONS,
        ...BASKETBALL_COMBINED_EXCLUSIONS,
        ...BASKETBALL_NON_TOTAL_EXCLUSIONS,
        /\b(?:offensive|defensive)\s+rebounds\b/i,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['rebounds'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'NBA',
    key: 'three_points_made',
    label: 'Three-Pointers Made',
    providerMarketTypeIds: ['12663', '12206'],
    providerMarketTypeNames: ['Three Pointers Made O/U', 'Three Pointers Made Milestones'],
    providerAliases: ['three pointers made', '3-pointers made', 'threes made'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'any',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        ...PERIOD_LIMITED_EXCLUSIONS,
        ...BASKETBALL_COMBINED_EXCLUSIONS,
        ...BASKETBALL_NON_TOTAL_EXCLUSIONS,
        /\bthree pointers attempted\b/i,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['three_points_made'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'NBA',
    key: 'assists',
    label: 'Assists',
    providerMarketTypeIds: ['12661', '12190'],
    providerMarketTypeNames: ['Assists O/U', 'Assists Milestones'],
    providerAliases: ['assists o/u', 'assists milestones'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'any',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        ...PERIOD_LIMITED_EXCLUSIONS,
        ...BASKETBALL_COMBINED_EXCLUSIONS,
        ...BASKETBALL_NON_TOTAL_EXCLUSIONS,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['assists'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    /*
     * Friday's Combo King. DK sells this exact market as "Points + Rebounds" — a single priced
     * O/U (12670) plus a milestone ladder (12193) — so it is one market to us, not two picks
     * added together. `settlementCombines: 'sum'` tells settlement to ADD the player's points
     * and rebounds before comparing to the frozen threshold.
     *
     * The `+ Assists` variants are excluded by name: "Points + Rebounds + Assists" contains
     * "Points + Rebounds" as a substring and would otherwise be frozen as this market, pricing
     * a three-stat line against a two-stat settlement.
     */
    sport: 'NBA',
    key: 'points_rebounds',
    label: 'Points + Rebounds',
    providerMarketTypeIds: ['12670', '12193'],
    providerMarketTypeNames: ['Points + Rebounds O/U', 'Points + Rebounds Milestones'],
    providerAliases: ['points + rebounds'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'any',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        ...PERIOD_LIMITED_EXCLUSIONS,
        ...BASKETBALL_NON_TOTAL_EXCLUSIONS,
        /\+\s*assists\b/i,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['points', 'rebounds'],
    settlementCombines: 'sum',
    implementationStatus: 'verified_settlement_ready',
  },
  {
    /*
     * Saturday's Double-Double King. A Yes/No market with NO line at all (`points` is null on
     * the selection), so there is no threshold to freeze and `over_or_true` grades it on the
     * settled value being >= 1 — the same shape as NFL anytime touchdown.
     *
     * The feed publishes no `double_double` stat, so settlement COMPUTES it from the component
     * stats it does publish (see `resolveDoubleDoubleValue` in NFLPropSettlementProvider):
     * 1 when two or more of points/rebounds/assists/steals/blocks each reach 10, else 0.
     */
    sport: 'NBA',
    key: 'double_double',
    label: 'Double-Double',
    providerMarketTypeIds: ['12667'],
    providerMarketTypeNames: ['Double-Double'],
    providerAliases: ['double-double', 'double double'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'any',
      selectionModes: ['yes'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        ...PERIOD_LIMITED_EXCLUSIONS,
        /\btriple[- ]double\b/i,
      ],
    },
    outcomeType: 'yes_no',
    lineRequired: false,
    defaultOperator: 'over_or_true',
    settlementStatTypes: ['double_double'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    /*
     * Not in the seven-day cadence, but DK's deepest basketball combo market and already
     * mapped, so a contest can select it without another capture run.
     */
    sport: 'NBA',
    key: 'points_rebounds_assists',
    label: 'Points + Rebounds + Assists',
    providerMarketTypeIds: ['12672', '12194'],
    providerMarketTypeNames: [
      'Points + Rebounds + Assists O/U',
      'Points + Rebounds + Assists Milestones',
    ],
    providerAliases: ['points + rebounds + assists'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'any',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        ...PERIOD_LIMITED_EXCLUSIONS,
        ...BASKETBALL_NON_TOTAL_EXCLUSIONS,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['points', 'rebounds', 'assists'],
    settlementCombines: 'sum',
    implementationStatus: 'verified_settlement_ready',
  },
  {
    /*
     * Steals and blocks carry no captured ids: DK offered neither on the WNBA slate read on
     * 2026-09-16. They stay registered — existing NBA contests may already select them — but
     * with empty ids they match by ALIAS only, which is why they keep `inferred` status.
     */
    sport: 'NBA',
    key: 'steals',
    label: 'Steals',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['steals'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'any',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        ...PERIOD_LIMITED_EXCLUSIONS,
        ...BASKETBALL_COMBINED_EXCLUSIONS,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['steals'],
    implementationStatus: 'inferred',
  },
  {
    sport: 'NBA',
    key: 'blocks',
    label: 'Blocks',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['blocks'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'any',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        ...PERIOD_LIMITED_EXCLUSIONS,
        ...BASKETBALL_COMBINED_EXCLUSIONS,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['blocks'],
    implementationStatus: 'inferred',
  },
];

// NCAAMB reuses NBA keys with same definitions
for (const def of [...PROP_MARKET_REGISTRY]) {
  if (def.sport === 'NBA') {
    PROP_MARKET_REGISTRY.push({ ...def, sport: 'NCAAMB' });
  }
}

export function getPropMarketDefinitionsForSport(
  sport: string,
  options?: { settlementReadyOnly?: boolean }
): PropMarketDefinition[] {
  const code = sport.trim().toUpperCase();
  return PROP_MARKET_REGISTRY.filter(d => {
    if (d.sport !== code) return false;
    if (options?.settlementReadyOnly) {
      return d.implementationStatus === 'verified_settlement_ready';
    }
    return d.implementationStatus !== 'unsupported';
  });
}

export function getPropMarketDefinition(
  sport: string,
  key: string
): PropMarketDefinition | undefined {
  const code = sport.trim().toUpperCase();
  return PROP_MARKET_REGISTRY.find(d => d.sport === code && d.key === key);
}

export function resolveAllowedPropMarketKeys(
  sport: string,
  allowedMarketKeys: string[] | null | undefined,
  fallbackPropStatType?: string | null
): string[] {
  if (allowedMarketKeys && allowedMarketKeys.length > 0) {
    return [...new Set(allowedMarketKeys.filter(Boolean))];
  }
  if (fallbackPropStatType) return [fallbackPropStatType];
  return getPropMarketDefinitionsForSport(sport, { settlementReadyOnly: true }).map(d => d.key);
}

export type MarketMatchInput = {
  marketTypeId?: string | null;
  marketTypeName?: string | null;
  marketName?: string | null;
  category?: string | null;
  tags?: string[] | null;
  hasParticipant?: boolean;
};

export function marketMatchesDefinition(
  market: MarketMatchInput,
  def: PropMarketDefinition
): boolean {
  const label = [market.marketName, market.marketTypeName, market.category, ...(market.tags ?? [])]
    .filter(Boolean)
    .join(' ');

  for (const exclude of def.contextGuards.excludeNamePatterns ?? []) {
    if (exclude.test(label) || (market.marketName && exclude.test(market.marketName))) {
      return false;
    }
  }

  const typeId = market.marketTypeId ? String(market.marketTypeId) : null;
  const typeName = (market.marketTypeName ?? '').trim().toLowerCase();
  const matchedById = typeId != null && def.providerMarketTypeIds.includes(typeId);
  const matchedByTypeName =
    typeName.length > 0 &&
    def.providerMarketTypeNames.some(n => n.trim().toLowerCase() === typeName);

  if (!matchedById && !matchedByTypeName) {
    /*
     * Alias regexes are a FALLBACK for payloads that don't declare a market type — never a
     * way to overrule a declared type id. Once a definition carries verified ids, a market
     * declaring some OTHER id is a different market, full stop: on the 2026-09-09 WNF slate
     * DK posted "Receiving Yards O/U - 1st Half" (15522) and "- 1st Quarter" (15516), both
     * tagged PlayerProps, and by alias alone they matched `receiving_yards` — freezing a
     * half's line as a full-game prop. Grading would then compare a 1H threshold against
     * full-game stats and look plausibly, silently right.
     */
    if (def.providerMarketTypeIds.length > 0 && typeId != null) return false;

    const matchedByAlias = def.providerAliases.some(alias =>
      new RegExp(alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i').test(label)
    );
    if (!matchedByAlias) return false;
  }

  if (def.contextGuards.requireTeamPropsTag) {
    // DK tags these `TeamProps`. Required rather than inferred from the name, because the name
    // is "{TEAM}: Team Total Runs" and a prefix is a weaker signal than the tag beside it.
    if (!(market.tags ?? []).some(t => /teamprops/i.test(t))) return false;
  }

  if (def.contextGuards.requirePlayerPropsTag) {
    const tags = market.tags ?? [];
    const hasPlayerProps = tags.some(t => /playerprops/i.test(t));
    const hasParticipant = Boolean(market.hasParticipant);
    // Allow participant or extractable player-prop name when tags omitted in tests/feeds
    if (!hasPlayerProps && !hasParticipant && !looksLikeNamedPlayerProp(market.marketName ?? '')) {
      return false;
    }
  }

  return true;
}

function looksLikeNamedPlayerProp(name: string): boolean {
  if (!name) return false;
  // "{First} {Last} Hits O/U" style
  return /\b[A-Z][a-z]+(?:\s+[A-Z][a-z'.-]+)+\b/.test(name) && !/^(Total|Team|Moneyline|Spread)\b/i.test(name);
}

export function findMatchingPropDefinitions(
  sport: string,
  market: MarketMatchInput,
  allowedKeys?: string[] | null
): PropMarketDefinition[] {
  const defs = getPropMarketDefinitionsForSport(sport).filter(d => {
    if (!allowedKeys || allowedKeys.length === 0) return true;
    return allowedKeys.includes(d.key);
  });
  return defs.filter(d => marketMatchesDefinition(market, d));
}

/**
 * Every feed stat code a sportsbook prop market for this sport could settle against.
 *
 * This is the vocabulary a market BUILDER should offer, not the whole box-score catalogue.
 * The feed exposes ~330 NFL stats — pass hurries, poor throws, blocked-FG return touchdowns —
 * and a market built on any of them prices at zero, because no sportsbook lists a line for it.
 * Filtering the picker by this set is what stops an operator building a tile that Freeze can
 * never fill, which is only discoverable today by pricing the pool and reading the failures.
 *
 * Scoped by subject deliberately. A stat code alone does not say whose stat it is, and offering
 * a player code against a team builds a market the team grader can never find a value for.
 *
 * Returns an EMPTY array when the registry carries no markets of that scope for the sport —
 * NFL has no team props at all — so callers can say "none yet" rather than silently offering
 * everything. Callers must not fall back to the full catalogue on empty; that is the bug.
 *
 * `settlementOnlyStatTypes` are excluded for the same reason the ~330-stat catalogue is. A kick
 * return touchdown SETTLES anytime touchdown, but no book lists "kick return touchdowns over
 * 0.5", so a tile built on it prices at zero — the exact failure this filter exists to prevent.
 */
export function getPropSettlementStatCodes(
  sport: string,
  kind: 'player' | 'team' = 'player'
): string[] {
  const codes = new Set<string>();
  for (const def of getPropMarketDefinitionsForSport(sport)) {
    if (definitionScope(def) !== kind) continue;
    const settlementOnly = new Set(def.settlementOnlyStatTypes ?? []);
    for (const code of def.settlementStatTypes ?? []) {
      if (code && !settlementOnly.has(code)) codes.add(code);
    }
  }
  return [...codes];
}

/**
 * The markets a builder can turn into a tile, for a sport.
 *
 * Narrower than `getPropMarketDefinitionsForSport` by exactly the markets that need their codes
 * ADDED UP to settle (`settlementCombines: 'sum'`). All-purpose yards is rushing plus receiving,
 * and a tile carries one stat code, so it has no code it can carry. Offering it anyway produced
 * nothing at all — the operator ticked it, the build skipped it, and the only trace was a server
 * log line. Summing at grade time is the real fix; until then the picker stays honest.
 *
 * `settlementCombines: 'any'` IS offered. Its codes are alternative ways the feed records one
 * event rather than components of a total, so a tile built on any of them prices correctly, and
 * Bingo settles it by checking all of them (`anySettlementFeedCodesFor`, read by
 * `BingoPlayerOverUnderGrader`). This is what kept Anytime Touchdown off the Bingo picker: it
 * used to be built from the FIRST code alone, which graded a receiver's touchdown as a loss, so
 * the market was withheld until grading could check every code.
 */
export function getBuildablePropMarketDefinitions(sport: string): PropMarketDefinition[] {
  return getPropMarketDefinitionsForSport(sport).filter(def => def.settlementCombines !== 'sum');
}

/**
 * The settlement codes of an any-of market, for the stat code a tile was BUILT on.
 *
 * A tile stores ONE `statTypeCode`, which for an any-of market is only the first of several
 * equivalent codes. This answers "what else counts as the same event?" so a grader can check all
 * of them — without it, an Anytime Touchdown tile built on `rush_touchdowns` scores a receiver's
 * touchdown as zero, which is precisely why that market was withheld from Bingo's picker.
 *
 * Keyed on the CODE and not on a sport or a market key, because the code is the only one of the
 * three a graded question actually carries.
 *
 * Returns an EMPTY array for anything that is not an any-of market, so a caller reads empty as
 * "settle on the single code you already have" and every other market grades exactly as before.
 *
 * AMBIGUITY RESOLVES TO EMPTY. If a code is ALSO the settlement code of a market that settles on
 * one code, then a tile carrying it could be either market and nothing here can tell them apart
 * — so it is left alone rather than silently widened. `pass_touchdowns` was this case until
 * Anytime Touchdown stopped claiming it (a thrown touchdown is not a scored one); the guard
 * stays so the next market to overlap fails safe instead of quietly mis-grading.
 */
export function anySettlementFeedCodesFor(statTypeCode: string): string[] {
  if (!statTypeCode) return [];
  const match = PROP_MARKET_REGISTRY.find(
    def => def.settlementCombines === 'any' && (def.settlementStatTypes ?? []).includes(statTypeCode)
  );
  if (!match) return [];

  const claimedElsewhere = PROP_MARKET_REGISTRY.some(
    def => def !== match && !def.settlementCombines && (def.settlementStatTypes ?? []).includes(statTypeCode)
  );
  if (claimedElsewhere) return [];

  /*
   * Aggregates are dropped HERE rather than by the caller.
   *
   * This function's contract is "every code that settles this event, so a grader can check all
   * of them", and `settlementStatTypes` leads with `total_touchdowns` — the total OF the two
   * codes beside it. A caller that follows the contract and adds the list up double-counts every
   * score the moment the feed starts publishing that code. It was filtered in exactly one
   * consumer (Bingo's `anyOfFeedStatCodesFor`), which left the next one to rediscover it; this
   * function is exported from the package index.
   */
  const aggregates = new Set(match.settlementAggregateStatTypes ?? []);
  return (match.settlementStatTypes ?? []).filter(code => !aggregates.has(code));
}

